import type { ApiClient, RequestOptions } from './client';

export type FeedbackRating = 'up' | 'down';

export interface FeedbackReason {
  code: string;
  label: string;
  requires_comment: boolean;
}

export interface FeedbackReasonsResponse {
  reasons: FeedbackReason[];
}

export interface Feedback {
  message_id: string;
  rating: FeedbackRating;
  reason_code: string | null;
  comment: string | null;
  created_at: string;
  updated_at: string;
}

export interface FeedbackEnvelope {
  feedback: Feedback | null;
}

export interface SubmitFeedbackInput {
  session_id: string;
  rating: FeedbackRating;
  reason_code?: string;
  comment?: string;
}

export interface FeedbackApi {
  fetchReasons: (language: string, options?: RequestOptions) => Promise<FeedbackReason[]>;
  submit: (
    messageId: string,
    input: SubmitFeedbackInput,
    options?: RequestOptions,
  ) => Promise<Feedback>;
  get: (
    messageId: string,
    sessionId: string,
    options?: RequestOptions,
  ) => Promise<Feedback | null>;
  clear: (messageId: string, sessionId: string, options?: RequestOptions) => Promise<void>;
}

export function createFeedbackApi(client: ApiClient): FeedbackApi {
  return {
    async fetchReasons(language, options) {
      const res = await client.get<FeedbackReasonsResponse>(
        `/api/feedback/reasons?language=${encodeURIComponent(language)}`,
        options,
      );
      return res.reasons;
    },

    submit(messageId, input, options) {
      // Backend rule: `up` must NOT carry reason_code / comment. Strip
      // them client-side so an accidental leftover from a prior "down"
      // doesn't 422 the request.
      const body: SubmitFeedbackInput = { session_id: input.session_id, rating: input.rating };
      if (input.rating === 'down') {
        if (input.reason_code) body.reason_code = input.reason_code;
        const trimmed = input.comment?.trim();
        if (trimmed) body.comment = trimmed;
      }
      return client.put<Feedback>(
        `/api/chat/messages/${encodeURIComponent(messageId)}/feedback`,
        body,
        options,
      );
    },

    async get(messageId, sessionId, options) {
      const res = await client.get<FeedbackEnvelope>(
        `/api/chat/messages/${encodeURIComponent(messageId)}/feedback?session_id=${encodeURIComponent(sessionId)}`,
        options,
      );
      return res.feedback;
    },

    clear(messageId, sessionId, options) {
      return client.del(
        `/api/chat/messages/${encodeURIComponent(messageId)}/feedback?session_id=${encodeURIComponent(sessionId)}`,
        options,
      );
    },
  };
}
